import { logger } from '~/utils/logger'

export default function CustomLineChartSettings(
  QdtComponents,
  measureInfo,
  minMeasure,
  maxMeasure
) {
  if (process.client) {
    //const QdtComponents = require('qdt-components')

    const picasso = QdtComponents.picasso
    const components = picasso.components
    const settings = {}

    settings.scales = {
      x: {
        data: {
          extract: {
            field: 'qDimensionInfo/0'
          }
        },
        padding: 0.3
      },
      y: {
        data: {
          fields: []
        },
        expand: 1,
        padding: 1,
        invert: true
      }
    }
    // dynamically populate the fields to extract data from
    measureInfo.forEach((ignore, index) => {
      settings.scales.y.data.fields.push(`qMeasureInfo/${index}`)
    })

    let include = []
    if (minMeasure != null) include.push(minMeasure)
    if (maxMeasure != null) include.push(maxMeasure)
    if (include.length) settings.scales.y.include = include

    // interactions (needed for tooltip)
    settings.interactions = [
      {
        type: 'native',
        events: {
          mousemove(e) {
            const tooltip = this.chart.component('tooltip-custom')
            tooltip.emit('show', e, {
              data: settings.scales.y.data
            })
          },
          mouseleave() {
            const tooltip = this.chart.component('tooltip-custom')
            tooltip.emit('hide')
          },
          click(e) {
            logger.debug(settings.scales)
            // do nothing (we don't want users selecting points here)
            logger.debug('e', e)
            e.cancelBubble = true
            //e.defaultPrevented = true
            return false
          }
        }
      }
    ]

    settings.components = [
      {
        type: 'grid-line',
        y: {
          scale: 'y'
        },
        ticks: {
          show: true,
          stroke: 'lightgray'
        }
      },
      {
        type: 'axis',
        key: 'x-axis',
        dock: 'bottom',
        format: undefined,
        scale: 'x',
        settings: {
          align: 'auto',
          labels: {
            align: 0.5,
            justify: 0,
            mode: 'auto',
            show: true,
            maxLengthPx: 85
          },
          line: {
            show: true,
            paddingEnd: 10,
            paddingStart: 0
          },
          ticks: {
            margin: 0,
            show: true,
            tickSize: 5
          }
        }
      },
      {
        type: 'axis',
        key: 'y-axis',
        dock: 'left',
        format: undefined,
        scale: 'y',
        settings: {
          align: 'auto',
          labels: {
            align: 0.5,
            justify: 1,
            mode: 'auto',
            show: true
          },
          line: {
            show: true,
            paddingEnd: 0,
            paddingStart: 0
          },
          ticks: {
            margin: 0,
            show: true,
            tickSize: 5
          },
          formatter: {
            formatter: 'd3', // The type of formatter to use
            type: 'number', // The type of data to format
            format: '.1f' // Format pattern
          }
        }
      }
    ]

    measureInfo.forEach(({ color }, index) => {
      settings.components.push(
        // this bar is strictly for labels, make transparent
        components.box({
          key: `bar${index}`,
          displayOrder: 1 + index * 3,
          start: 0,
          end: {
            field: `qMeasureInfo/${index}`
          },
          fill: 'transparent',
          stroke: 'transparent'
        })
      )
      settings.components.push(
        components.line({
          key: `line${index}`,
          displayOrder: 2 + index * 3,
          y: {
            field: `qMeasureInfo/${index}`
          },
          fill: color,
          stroke: color
        })
      )
      settings.components.push(
        components.point({
          key: `point${index}`,
          displayOrder: 3 + index * 3,
          y: {
            field: `qMeasureInfo/${index}`
          },
          fill: color,
          stroke: color
        })
      )
      // labels should go above everything in display order
      settings.components.push({
        type: 'labels',
        key: `labels${index}`,
        dock: null,
        displayOrder: measureInfo.length * 3 + index + 1,
        settings: {
          sources: [
            {
              component: `bar${index}`,
              selector: 'rect',
              strategy: {
                type: 'bar',
                settings: {
                  direction: 'down',
                  align: 0.5,
                  justify: 0,
                  fontSize: 12,
                  labels: [
                    {
                      placements: [
                        {
                          position: 'inside',
                          fill: '#000000'
                        }
                      ],
                      label: ({ data }) => {
                        return (
                          data?.end?.label ||
                          data?.end?.value.toString().substr(0, 3) ||
                          ''
                        )
                      }
                    }
                  ]
                }
              }
            }
          ]
        }
      })
    })

    settings.components.push({
      key: 'cat',
      type: 'legend-cat',
      scale: {
        type: 'categorical-color',
        data: measureInfo.map(({ label }) => label),
        range: measureInfo.map(({ color }) => color)
      },
      dock: 'right'
    })

    // tooltip
    settings.components.push({
      displayOrder: 15,
      key: 'tooltip-custom',
      type: 'tooltip',
      settings: {
        filter: function filter(nodes) {
          return nodes.filter(function(node) {
            return node.type === 'circle'
          })
        },
        extract: function extract({ node }) {
          return {
            data: node.data,
            key: node.key
          }
        },
        // Generate virtual nodes
        content: ({ h, data }) => {
          var html = ''
          const title = measureInfo[parseInt(data[0].key.substr(-1))].label

          const body = ''
            .concat(data[0].data.label, ': ')
            .concat(data[0].data.y.label)

          // Stacked
          html = h('div.qdt-tooltip-header', {}, [
            h(
              'div.qdt-tooltip-header-title',
              {
                align: 'center',
                style: {
                  'border-bottom': '1px solid rgba(255,255,255,0.2)',
                  'padding-bottom': '5px',
                  display: 'block'
                }
              },
              title
            ),
            h(
              'div.qdt-tooltip-header-measure',
              {
                align: 'center',
                style: {
                  'padding-top': '55px'
                }
              },
              body
            )
          ])

          return h(
            'div',
            {
              display: 'table'
            },
            html
          )
        },
        afterShow: function afterShow({ element }) {
          element.children[0].style.opacity = 1
          element.children[1].style.opacity = 1 // debugger;
        },
        onHide: function onHide({ element }) {
          element.children[0].style.opacity = 0
          element.children[1].style.opacity = 0
        },
        placement: {
          type: 'pointer',
          area: 'target'
        }
      },
      style: {
        content: {
          'border-spacing': '4px',
          opacity: 0,
          transition: 'opacity 150ms ease-in'
        },
        arrow: {
          opacity: 0,
          transition: 'opacity 150ms ease-in'
        }
      }
    })

    return settings
  } else {
    return {}
  }
}
